import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  IndexTable,
  IndexFilters,
  useSetIndexFiltersMode,
  Badge,
  Text,
  Banner,
  useIndexResourceState,
  type IndexFiltersProps,
  type TabProps,
} from '@shopify/polaris';
import {
  inquiryService,
  type InquiryListItem,
  type InquiryStatus,
  type InquiryType,
} from '../../services/inquiry.service';

const STATUS_OPTIONS: { label: string; value: InquiryStatus }[] = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Under Review', value: 'UNDER_REVIEW' },
  { label: 'Quote Sent', value: 'QUOTE_SENT' },
  { label: 'Quote Accepted', value: 'QUOTE_ACCEPTED' },
  { label: 'Quote Declined', value: 'QUOTE_DECLINED' },
  { label: 'Revision Requested', value: 'QUOTE_REVISION_REQUESTED' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Cancelled', value: 'CANCELLED' },
];

const TYPE_OPTIONS: { label: string; value: InquiryType }[] = [
  { label: 'Sample Request', value: 'SAMPLE_REQUEST' },
  { label: 'Bulk Order', value: 'BULK_ORDER' },
];

function getStatusBadge(status: InquiryStatus) {
  const toneMap: Record<InquiryStatus, 'warning' | 'info' | 'success' | 'critical' | 'attention' | undefined> = {
    PENDING: 'warning',
    UNDER_REVIEW: 'info',
    QUOTE_SENT: 'info',
    QUOTE_ACCEPTED: 'success',
    QUOTE_DECLINED: 'critical',
    QUOTE_REVISION_REQUESTED: 'attention',
    IN_PROGRESS: 'info',
    COMPLETED: 'success',
    CANCELLED: 'critical',
  };

  const labelMap: Record<InquiryStatus, string> = {
    PENDING: 'Pending',
    UNDER_REVIEW: 'Under Review',
    QUOTE_SENT: 'Quote Sent',
    QUOTE_ACCEPTED: 'Accepted',
    QUOTE_DECLINED: 'Declined',
    QUOTE_REVISION_REQUESTED: 'Revision',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
  };

  return <Badge tone={toneMap[status]}>{labelMap[status]}</Badge>;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function InquiryListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [inquiries, setInquiries] = useState<InquiryListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);

  // Filters
  const [queryValue, setQueryValue] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | ''>(
    (searchParams.get('status') as InquiryStatus) || ''
  );
  const [typeFilter, setTypeFilter] = useState<InquiryType | ''>(
    (searchParams.get('type') as InquiryType) || ''
  );
  const [page, setPage] = useState(1);

  const { mode, setMode } = useSetIndexFiltersMode();
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(inquiries as unknown as {[key: string]: unknown}[]);

  const loadInquiries = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await inquiryService.list({
        page,
        limit: 20,
        search: queryValue || undefined,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      });
      setInquiries(result.data);
      setTotal(result.meta.total);
    } catch (err) {
      setError('Failed to load inquiries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, queryValue, statusFilter, typeFilter]);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const handleFiltersQueryChange = useCallback((value: string) => {
    setQueryValue(value);
    setPage(1);
  }, []);

  const handleQueryValueRemove = useCallback(() => {
    setQueryValue('');
    setPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((value: string[]) => {
    setStatusFilter(value[0] as InquiryStatus || '');
    setPage(1);
  }, []);

  const handleTypeFilterChange = useCallback((value: string[]) => {
    setTypeFilter(value[0] as InquiryType || '');
    setPage(1);
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    setQueryValue('');
    setStatusFilter('');
    setTypeFilter('');
    setPage(1);
  }, []);

  const filters: IndexFiltersProps['filters'] = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange([e.target.value])}
          style={{ width: '100%', padding: '8px' }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ),
      shortcut: true,
    },
    {
      key: 'type',
      label: 'Type',
      filter: (
        <select
          value={typeFilter}
          onChange={(e) => handleTypeFilterChange([e.target.value])}
          style={{ width: '100%', padding: '8px' }}
        >
          <option value="">All Types</option>
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ),
      shortcut: true,
    },
  ];

  const appliedFilters: IndexFiltersProps['appliedFilters'] = [];
  if (statusFilter) {
    appliedFilters.push({
      key: 'status',
      label: `Status: ${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label}`,
      onRemove: () => setStatusFilter(''),
    });
  }
  if (typeFilter) {
    appliedFilters.push({
      key: 'type',
      label: `Type: ${TYPE_OPTIONS.find((o) => o.value === typeFilter)?.label}`,
      onRemove: () => setTypeFilter(''),
    });
  }

  const tabs: TabProps[] = [
    { id: 'all', content: 'All', accessibilityLabel: 'All inquiries' },
    { id: 'pending', content: 'Pending', accessibilityLabel: 'Pending inquiries' },
    { id: 'in-progress', content: 'In Progress', accessibilityLabel: 'In progress inquiries' },
    { id: 'completed', content: 'Completed', accessibilityLabel: 'Completed inquiries' },
  ];

  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = useCallback((tabIndex: number) => {
    setSelectedTab(tabIndex);
    const tabStatuses: Record<number, InquiryStatus | ''> = {
      0: '',
      1: 'PENDING',
      2: 'IN_PROGRESS',
      3: 'COMPLETED',
    };
    setStatusFilter(tabStatuses[tabIndex] || '');
    setPage(1);
  }, []);

  const rowMarkup = inquiries.map((inquiry, index) => (
    <IndexTable.Row
      id={inquiry.id}
      key={inquiry.id}
      selected={selectedResources.includes(inquiry.id)}
      position={index}
      onClick={() => navigate(`/inquiries/${inquiry.id}`)}
    >
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {inquiry.referenceNo}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {inquiry.user.name || inquiry.user.email}
        </Text>
        {inquiry.user.companyName && (
          <Text variant="bodySm" tone="subdued" as="p">
            {inquiry.user.companyName}
          </Text>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={inquiry.type === 'BULK_ORDER' ? 'info' : undefined}>
          {inquiry.type === 'BULK_ORDER' ? 'Bulk Order' : 'Sample Request'}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{getStatusBadge(inquiry.status)}</IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {inquiry.itemCount} item{inquiry.itemCount !== 1 ? 's' : ''}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={inquiry.hasQuote ? 'success' : undefined}>
          {inquiry.hasQuote ? 'Yes' : 'No'}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{formatDate(inquiry.createdAt)}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page title="Inquiries" fullWidth>
      {error && (
        <Banner tone="critical" onDismiss={() => setError('')}>
          {error}
        </Banner>
      )}

      <Card padding="0">
        <IndexFilters
          queryValue={queryValue}
          queryPlaceholder="Search by reference, customer name, email..."
          onQueryChange={handleFiltersQueryChange}
          onQueryClear={handleQueryValueRemove}
          tabs={tabs}
          selected={selectedTab}
          onSelect={handleTabChange}
          filters={filters}
          appliedFilters={appliedFilters}
          onClearAll={handleFiltersClearAll}
          mode={mode}
          setMode={setMode}
          canCreateNewView={false}
        />
        <IndexTable
          resourceName={{ singular: 'inquiry', plural: 'inquiries' }}
          itemCount={inquiries.length}
          selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
          onSelectionChange={handleSelectionChange}
          headings={[
            { title: 'Reference' },
            { title: 'Customer' },
            { title: 'Type' },
            { title: 'Status' },
            { title: 'Items' },
            { title: 'Quote' },
            { title: 'Date' },
          ]}
          loading={loading}
          pagination={{
            hasNext: page * 20 < total,
            hasPrevious: page > 1,
            onNext: () => setPage((p) => p + 1),
            onPrevious: () => setPage((p) => p - 1),
          }}
        >
          {rowMarkup}
        </IndexTable>
      </Card>
    </Page>
  );
}
